import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Container,
  Grid,
  Paper,
  Button,
  useMediaQuery,
  useTheme,
  CircularProgress,
} from '@mui/material';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import CategoryIcon from '@mui/icons-material/Category';
import CelebrationIcon from '@mui/icons-material/Celebration';
import PublicIcon from '@mui/icons-material/Public';
import InventoryIcon from '@mui/icons-material/Inventory';
import EmailIcon from '@mui/icons-material/Email';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import QrCodeIcon from '@mui/icons-material/QrCode';
import FingerprintIcon from '@mui/icons-material/Fingerprint';
import LinkIcon from '@mui/icons-material/Link';
import BrandingWatermarkIcon from '@mui/icons-material/BrandingWatermark';

import './index.css';

const ProductPage = ({ productList, langCode }) => {
  const { ean } = useParams();
  const product = productList.find((p) => p.product_ean === ean);
  const [topBarHeight, setTopBarHeight] = useState(0);
  const topBarRef = useRef(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Set the top bar height on mount and on window resize
  useEffect(() => {
    if (topBarRef.current) {
      setTopBarHeight(topBarRef.current.offsetHeight);
    }
    const handleResize = () => {
      if (topBarRef.current) {
        setTopBarHeight(topBarRef.current.offsetHeight);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  if (!product) {
    return (
      <Container
        sx={{
          backgroundColor: 'black',
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CircularProgress sx={{ color: '#ec008c' }} size={60} thickness={4} />
      </Container>
    );
  }

  return (
    <Container
      sx={{
        backgroundColor: 'black',
        paddingTop: `${topBarHeight + 20}px`,
        paddingBottom: 10,
        minHeight: '100vh',
        position: 'relative',
      }}
      maxWidth="lg"
    >
      {/* Instant Buy Section */}
      <Box
        sx={{
          marginBottom: 4,
          color: 'white',
          display: 'flex',
          justifyContent: 'flex-end',
          position: 'sticky',
          top: `${topBarHeight + 90}px`,
          zIndex: 110,
        }}
      >
        <Paper
          sx={{
            padding: 3,
            backgroundColor: '#1a1a1a',
            borderRadius: '12px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.5)',
            textAlign: 'center',
            color: 'white',
            width: 'auto',
            maxWidth: '300px',
          }}
        >

<Button
  onClick={() => {
    const baseUrl = 'https://notedin.com/instantbuy';

    const trimImagePath = (url) => {
      if (!url) return '';
      const parts = url.split('/');
      const last3 = parts.slice(-3).join('/');
      return encodeURIComponent(`/${last3}`);
    };

    const queryObject = {
      'product.country': product.country || '',
      'product.seller_email': product.seller_email || '',
      'product.seller_id': product.seller_id || '',
      'product.product_ean': product.product_ean || '',
      'product.product_sku': product.product_sku || '',
      'product.brand': product.brand || '',
      'product.landing_page': product.landing_page || '',
      'product.product_main_category': product.product_main_category || '',
      // Ensure that product_sub_category is checked to avoid undefined error
      'product.product_sub_category': product.product_sub_category || '',
      'product.in_stock': product.in_stock || '',
      'product.shipping': product.shipping || '',
      'product.product_description': (product.product_description || '').slice(0, 200),
      'product.image_url1': product.image_url1 || '',
      'product.image_url2': product.image_url2 || '',
      'product.image_url3': product.image_url3 || '',
      'product.image_url4': product.image_url4 || '',
      'product.image_url5': product.image_url5 || '',
      'product.image_url6': product.image_url6 || '',
      'product.image_url7': product.image_url7 || '',
      'product.image_url8': product.image_url8 || '',
      'product.image_url9': product.image_url9 || '',
      'product.image_url10': product.image_url10 || '',
      'msg_recipient': product.seller_email || '',
    };

    // ✅ Conditionally add `currency`, `price`, and `Lang_Code`
    if (product.currency) {
      queryObject['product.currency'] = product.currency;
    }
    if (product.price_gross !== undefined && product.price_gross !== null && product.price_gross !== '') {
      queryObject['product.price_gross'] = product.price_gross;
    }
    if (product.langCode) {
      queryObject['Lang_Code'] = product.langCode;
    }

    // Build the query string
    const queryParams = new URLSearchParams(queryObject).toString();

    // Redirect to the URL with the correct query parameters
    window.location.href = `${baseUrl}?${queryParams}`;
  }}
  variant="contained"
  size="large"
  sx={{
    fontWeight: 'bold',
    paddingY: 1.5,
    backgroundColor: '#ec008c',
    color: 'white',
    boxShadow: '0 4px 12px rgba(255, 255, 255, 0.2)',
    '&:hover': {
      backgroundColor: '#ff66b2',
      boxShadow: '0 6px 18px rgba(255, 255, 255, 0.4)',
    },
    width: '100%',
    maxWidth: '200px',
    margin: '0 auto',
  }}
>
  🚀 Instant Buy
</Button>







<Typography variant="h5" sx={{ color: 'white', marginTop: 2 }}>
  {product.sale_price} {product.currency}
</Typography>

        </Paper>
      </Box>

      {/* Main Product Layout */}
      <Grid container spacing={isMobile ? 2 : 4}>
        <Grid item xs={12} md={7}>
          <Box
            className="gallery"
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
              gap: 2,
            }}
          >
            {[product.image_url1, product.image_url2, product.image_url3, product.image_url4, product.image_url5, product.image_url6, product.image_url7, product.image_url8, product.image_url9, product.image_url0]
              .filter(Boolean)
              .map((url, idx) => (
                <a
  key={idx}
  href={url}
  target="_blank"
  rel="noopener noreferrer"
  style={{ display: 'block' }}
>
  <img
    src={url}
    alt={`Product ${idx + 1}`}
    style={{
      width: '100%',
      height: 'auto',
      borderRadius: '12px',
      boxShadow: '0 4px 8px rgba(0,0,0,0.4)',
      objectFit: 'cover',
    }}
  />
</a>

              ))}
          </Box>
        </Grid>

        <Grid item xs={12} md={5}>
          <Paper
            variant="outlined"
            sx={{
              padding: 2,
              backgroundColor: '#1a1a1a',
              borderRadius: '12px',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.5)',
              color: 'white',
            }}
          >
            <Typography variant="subtitle1" gutterBottom sx={{ mb: 2 }}>
              Product Info
            </Typography>
            <Grid container spacing={isMobile ? 1 : 2}>
              {[
                { icon: <BrandingWatermarkIcon />, label: product.brand },
                { icon: <CategoryIcon />, label: product.product_main_category },
                { icon: <CelebrationIcon />, label: product.product_sub_category },
                { icon: <PublicIcon />, label: product.country },
                { icon: <LocalShippingIcon />, label: product.shipping === 'yes' ? 'Shipping Available' : 'No Shipping' },
                { icon: <EmailIcon />, label: product.seller_email },
                {
                  icon: <InventoryIcon />,
                  label: product.in_stock ? (
                    <Box component="span" color="lightgreen">
                      <CheckCircleIcon sx={{ verticalAlign: 'middle', mr: 0.5 }} fontSize="small" />
                      In Stock
                    </Box>
                  ) : (
                    <Box component="span" color="red">
                      <CancelIcon sx={{ verticalAlign: 'middle', mr: 0.5 }} fontSize="small" />
                      Out of Stock
                    </Box>
                  ),
                },
                { icon: <MonetizationOnIcon />, label: `${product.price_gross} ${product.currency}` },
                { icon: <QrCodeIcon />, label: product.product_ean },
                { icon: <FingerprintIcon />, label: product.product_sku },
                {
                  icon: <LinkIcon />,
                  label: (
                    <a href={product.landing_page} target="_blank" rel="noopener noreferrer" style={{ color: 'white' }}>
                      Product Link
                    </a>
                  ),
                },
              ].map((item, i) => (
                <Grid item xs={12} sm={6} key={i}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {item.icon}
                    <Typography variant="body2" sx={{ marginLeft: 1 }}>
                      {item.label}
                    </Typography>
                  </Box>
                </Grid>
              ))}

              {/* Product Description */}
              <Box
                sx={{
                  padding: 2,
                  backgroundColor: '#333',
                  borderRadius: '8px',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.5)',
                  marginTop: 3,
                }}
              >
                <Typography variant="body1" sx={{ wordWrap: 'break-word' }}>
                  {product.product_description}
                </Typography>
              </Box>
            </Grid>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default ProductPage;
